/* eslint-disable no-param-reassign */
import type { PayloadAction } from '@reduxjs/toolkit';
import { createSlice } from '@reduxjs/toolkit';
import type {
  GetCADashboardResponse,
  GetCAStatsResponse,
  CALeaderboardEntrySub,
} from 'src/openapi';

export interface CAWeekHistoryItem {
  week_id: string;
  week_number: number;
  start_date: string | null;
  end_date: string | null;
  status: string;
  gems_earned: number;
  tasks_completed: number;
  total_tasks: number;
  conversions: number;
  rank: number | null;
}

export interface CAWeekHistoryResponse {
  weeks: CAWeekHistoryItem[];
  cohort: Record<string, any> | null;
  participant: Record<string, any> | null;
}

interface CAState {
  dashboard: GetCADashboardResponse | null;
  weeklyLeaderboard: CALeaderboardEntrySub[];
  overallLeaderboard: CALeaderboardEntrySub[];
  stats: GetCAStatsResponse | null;
  weekHistory: CAWeekHistoryResponse | null;
  loading: boolean;
  // Unread Messages-widget counts, split by tab, surfaced on the global
  // "Ambassador HQ" sidebar pin (the widget itself is only mounted on the CA
  // dashboard, so the count has to live in the store to show app-wide).
  inboxUnread: { chat: number; ann: number };
}

const initialState: CAState = {
  dashboard: null,
  weeklyLeaderboard: [],
  overallLeaderboard: [],
  stats: null,
  weekHistory: null,
  loading: false,
  inboxUnread: { chat: 0, ann: 0 },
};

const campusAmbassadorSlice = createSlice({
  name: 'campusAmbassador',
  initialState,
  reducers: {
    setCADashboard(state, action: PayloadAction<GetCADashboardResponse>) {
      state.dashboard = action.payload;
    },
    setCAWeeklyLeaderboard(
      state,
      action: PayloadAction<CALeaderboardEntrySub[]>,
    ) {
      state.weeklyLeaderboard = action.payload;
    },
    setCAStats(state, action: PayloadAction<GetCAStatsResponse>) {
      state.stats = action.payload;
    },
    setCAOverallLeaderboard(
      state,
      action: PayloadAction<CALeaderboardEntrySub[]>,
    ) {
      state.overallLeaderboard = action.payload;
    },
    setCAWeekHistory(state, action: PayloadAction<CAWeekHistoryResponse>) {
      state.weekHistory = action.payload;
    },
    setCALoading(state, action: PayloadAction<boolean>) {
      state.loading = action.payload;
    },
    setCAInboxUnread(
      state,
      action: PayloadAction<{ chat: number; ann: number }>,
    ) {
      state.inboxUnread = action.payload;
    },
    bumpCAInboxUnread(state, action: PayloadAction<'chat' | 'ann'>) {
      state.inboxUnread[action.payload] += 1;
    },
    clearCAInboxUnread(state, action: PayloadAction<'chat' | 'ann'>) {
      state.inboxUnread[action.payload] = 0;
    },
    resetCA(state) {
      state.dashboard = null;
      state.weeklyLeaderboard = [];
      state.overallLeaderboard = [];
      state.stats = null;
      state.weekHistory = null;
      state.inboxUnread = { chat: 0, ann: 0 };
    },
  },
});

export const {
  setCADashboard,
  setCAWeeklyLeaderboard,
  setCAOverallLeaderboard,
  setCAStats,
  setCAWeekHistory,
  setCALoading,
  setCAInboxUnread,
  bumpCAInboxUnread,
  clearCAInboxUnread,
  resetCA,
} = campusAmbassadorSlice.actions;

export default campusAmbassadorSlice.reducer;
